// Roster + display helpers, shared by the admin form and the public
// display. Reads the candidates DB table (single source of truth) and
// builds per-district candidate lists honouring BOTH `district` and
// `district_2` — a two-district candidate appears in two lists but
// stays one DB row (one id).
//
// Party display goes through normalisePartyName so PL/PN/Momentum chips
// match every other surface on the site.

import useSWR from 'swr';
import { useMemo } from 'react';
import type { CandidateRow } from '../../db/types';
import { normalisePartyName, type CanonicalParty } from '../../../api/_lib/parties';
import { useResultsDemoMode } from '../../data/demo/contract';
import {
  DEMO_D13_CANDIDATES,
  DEMO_D13_DISTRICT_ID,
} from '../../data/demo/results.demo';

interface CandidatesApi {
  candidates: CandidateRow[];
}

const fetcher = async (url: string): Promise<CandidatesApi> => {
  const res = await fetch(url, { cache: 'no-store' });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return (await res.json()) as CandidatesApi;
};

export interface RosterCandidate {
  id: string;
  name: string;
  partyRaw: string;
  party: CanonicalParty;
}

export function useResultsRoster(): {
  roster: CandidateRow[];
  byDistrict: (districtId: number) => RosterCandidate[];
  byId: (id: string) => RosterCandidate | undefined;
  isLoading: boolean;
} {
  const isDemo = useResultsDemoMode();
  // SWR hook unconditional (rules of hooks) — we just ignore the
  // result in demo mode. Cheap: identical key dedupes across mounts.
  const { data, isLoading } = useSWR<CandidatesApi>(
    '/api/candidates',
    fetcher,
    { revalidateOnFocus: true, dedupingInterval: 60_000 },
  );
  const roster = data?.candidates ?? [];

  const byId = useMemo(() => {
    if (isDemo) {
      const m = new Map<string, RosterCandidate>();
      for (const c of DEMO_D13_CANDIDATES) {
        m.set(c.id, {
          id: c.id,
          name: c.name,
          partyRaw: c.party,
          party: c.party,
        });
      }
      return (id: string) => m.get(String(id));
    }
    const m = new Map<string, RosterCandidate>();
    for (const r of roster) {
      m.set(String(r.id), {
        id: String(r.id),
        name: r.display_name?.trim() || r.full_name,
        partyRaw: r.party,
        party: normalisePartyName(r.party),
      });
    }
    return (id: string) => m.get(String(id));
  }, [roster, isDemo]);

  const byDistrict = useMemo(() => {
    if (isDemo) {
      // Demo only populates D13 — every other district is empty.
      return (districtId: number): RosterCandidate[] => {
        if (districtId !== DEMO_D13_DISTRICT_ID) return [];
        return DEMO_D13_CANDIDATES.map((c) => ({
          id: c.id,
          name: c.name,
          partyRaw: c.party,
          party: c.party,
        }));
      };
    }
    return (districtId: number): RosterCandidate[] => {
      const out: RosterCandidate[] = [];
      for (const r of roster) {
        if (r.district === districtId || r.district_2 === districtId) {
          out.push({
            id: String(r.id),
            name: r.display_name?.trim() || r.full_name,
            partyRaw: r.party,
            party: normalisePartyName(r.party),
          });
        }
      }
      // Stable alpha-by-surname for tables; first-count round will
      // reorder rows by running total in the display.
      out.sort((a, b) => {
        const sa = surname(a.name);
        const sb = surname(b.name);
        return (
          sa.localeCompare(sb, 'en', { sensitivity: 'base' }) ||
          a.name.localeCompare(b.name, 'en', { sensitivity: 'base' })
        );
      });
      return out;
    };
  }, [roster, isDemo]);

  return {
    roster: isDemo ? [] : roster,
    byDistrict,
    byId,
    isLoading: isDemo ? false : isLoading,
  };
}

function surname(fullName: string): string {
  const parts = fullName.trim().split(/\s+/);
  return parts[parts.length - 1] ?? fullName;
}

/** Canonical hex for a party — keyed off CanonicalParty so map and
 *  seat-strip palettes stay in sync with the rest of the site. */
export function partyColour(party: CanonicalParty): string {
  switch (party) {
    case 'PL':
      return '#cf1b2b';
    case 'PN':
      return '#003da5';
    case 'ADPD':
      return '#2e8b4e';
    case 'Momentum':
      return '#e8b800';
    case 'Imperium':
      return '#8b2fc9';
    case 'Aħwa Maltin':
      return '#7a3b0a';
    case 'IND':
    case 'Other':
    default:
      return '#5d4037';
  }
}

/** Short label for party chips ("PL", "PN", "MOM", …) — display-only. */
export function partyChip(party: CanonicalParty): string {
  switch (party) {
    case 'Momentum':
      return 'MOM';
    case 'Imperium':
      return 'IMP';
    case 'Aħwa Maltin':
      return 'AM';
    case 'IND':
    case 'Other':
      return 'IND';
    default:
      return party; // PL / PN / ADPD
  }
}
